import { Link } from 'wouter';
import { trpc } from '../lib/trpc';
import { useAsync } from '../lib/useAsync';
import { Loading, ErrorBox } from '../components/admin/ui';

/**
 * Prévia "ver como meu cliente vê": mostra a tela Início do app do comprador com
 * os DADOS REAIS de um contrato da carteira da própria org. É só leitura (o
 * backend appCliente.previa é org-scoped e não expõe nenhuma ação), então a
 * incorporadora confere o que o comprador enxerga sem precisar de link mágico.
 */

const brl = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);
const dataBr = (iso: string | null) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export function PreviaCompradorPage() {
  const info = useAsync(() => trpc.appCliente.info.query());
  const compradorId = info.data?.compradorExemploId ?? null;
  const previa = useAsync(
    () => (compradorId ? trpc.appCliente.previa.query({ compradorId }) : Promise.resolve(null)),
    [compradorId],
  );

  if (info.loading || previa.loading) return <Loading />;
  if (info.error) return <ErrorBox msg={info.error} onRetry={info.reload} />;
  if (!compradorId || !previa.data) {
    return (
      <div className="mx-auto max-w-md p-6 text-center text-sm text-ink-3">
        A prévia precisa de ao menos um contrato importado na sua carteira.
        <div className="mt-4">
          <Link href="/app-cliente" className="text-accent">
            Voltar para App do Cliente
          </Link>
        </div>
      </div>
    );
  }

  const p = previa.data.painel;
  const cor = info.data?.corMarca || '#005C3D';

  return (
    <div className="mx-auto max-w-md">
      <div className="mb-4 flex items-center justify-between">
        <Link href="/app-cliente" className="text-sm text-ink-3 hover:text-ink">
          Voltar
        </Link>
        <span className="rounded-full bg-accent-soft px-2.5 py-0.5 text-xs font-semibold text-accent">
          Prévia
        </span>
      </div>
      <p className="mb-4 text-center text-xs text-ink-4">
        É isto que o seu cliente {previa.data.nome ? `(${previa.data.nome})` : ''} vê no aplicativo. Só leitura.
      </p>

      {/* Moldura do app do comprador */}
      <div className="overflow-hidden rounded-[28px] border border-line bg-bg-warm p-5">
        <div className="text-sm text-ink-3">Olá, {p.primeiroNome}</div>
        <div className="font-serif text-2xl leading-tight text-ink">{p.unidade}</div>

        {/* Patrimônio */}
        <div className="mt-4 rounded-2xl p-5 text-white" style={{ background: cor }}>
          <div className="text-sm opacity-90">Você já conquistou</div>
          <div className="font-serif text-4xl">{brl(p.valorPago)}</div>
          <div className="mt-1 text-sm opacity-90">de {brl(p.valorContrato)} do seu imóvel</div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/25">
            <div className="h-full rounded-full bg-white" style={{ width: `${Math.min(100, p.percentualQuitado)}%` }} />
          </div>
          <div className="mt-2 flex justify-between text-sm opacity-90">
            <span>{p.percentualQuitado}% quitado</span>
            <span>faltam {brl(p.saldoDevedor)}</span>
          </div>
        </div>

        {/* Próxima parcela + saldo */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white p-4">
            <div className="text-xs text-ink-4">Próxima parcela</div>
            <div className="font-serif text-xl text-ink">
              {p.proximaParcela ? brl(p.proximaParcela.valor) : 'tudo em dia'}
            </div>
            {p.proximaParcela?.vencimento && (
              <div className="text-xs text-ink-4">vence {dataBr(p.proximaParcela.vencimento)}</div>
            )}
          </div>
          <div className="rounded-2xl bg-white p-4">
            <div className="text-xs text-ink-4">Saldo devedor</div>
            <div className="font-serif text-xl text-ink">{brl(p.saldoDevedor)}</div>
            <div className="text-xs text-ink-4">
              {p.parcelasPagas} de {p.parcelasTotal} pagas
            </div>
          </div>
        </div>

        {/* Reajuste */}
        {p.reajuste && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-sm font-semibold text-amber-800">
              Seu reajuste anual chega em {p.reajuste.dias} dias
            </div>
            <div className="mt-1 text-sm text-ink-2">
              {p.reajuste.parcelaNova !== null ? (
                <>
                  Sua parcela vai passar de {brl(p.reajuste.parcelaAtual)} para {brl(p.reajuste.parcelaNova)}.
                </>
              ) : (
                <>O valor exato ainda vai ser confirmado.</>
              )}
            </div>
          </div>
        )}

        {/* Obra */}
        {p.obra && (
          <div className="mt-4 rounded-2xl bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs text-ink-4">Sua obra</div>
              {p.obra.percentual != null && <div className="text-sm font-semibold text-accent">{p.obra.percentual}% concluída</div>}
            </div>
            <div className="mt-1 font-serif text-lg text-ink">{p.obra.fase ?? 'Cronograma em preparação'}</div>
            {p.obra.entregaPrevista && (
              <div className="text-xs text-ink-4">Entrega prevista: {dataBr(p.obra.entregaPrevista)}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
